import { Duration } from "luxon";

import type { Summary, Tick } from "./FlightRecorder";

const TRUNCATION_SLACK = Duration.fromObject({ seconds: 1 });

const ms = (value: number): string => `${value.toFixed(1)}ms`;

/**
 * Renders a {@link Summary} as plain-text Markdown, compact enough to paste into a chat with an agent.
 *
 * Leads with where the time went (busy time, computes, frames), then what caused it (writes), then the worst ticks
 * whole, so a reader can go from symptom to cause without the raw data.
 */
export function formatReport(summary: Summary, context: Record<string, string | undefined> = {}): string {
  const lines: string[] = [];
  const seconds = Duration.fromMillis(summary.windowMs).as("seconds");

  // Headline: how much of the window the page spent reacting.
  lines.push(`# Flight recorder — last ${seconds}s`);
  if (isTruncated(summary)) {
    const coveredSeconds = Duration.fromMillis(summary.coveredMs).as("seconds");
    lines.push(`(only the last ${coveredSeconds.toFixed(0)}s are still held; totals cover that span)`);
  }
  for (const [key, value] of Object.entries(context)) if (value) lines.push(`${key}: ${value}`);
  lines.push(
    `ticks: ${summary.ticks}, busy: ${ms(summary.busyMs)}, long frames: ${summary.frames.count} ` +
      `(${ms(summary.frames.totalMs)}, blocking ${ms(summary.frames.blockingMs)})`,
    `renders: ${summary.renders.commits} commits, ${ms(summary.renders.totalMs)} (max ${ms(summary.renders.maxMs)}); ` +
      `table rows rendered ${summary.rows.rendered}, skipped ${summary.rows.skipped}`,
  );

  // Where compute time went.
  lines.push("", "## Computes (name | runs | changed | total | max)");
  for (const compute of summary.computes) {
    lines.push(
      `${compute.name} | ${compute.runs} | ${compute.changedRuns} | ${ms(compute.totalMs)} | ${ms(compute.maxMs)}`,
    );
  }

  // What triggered it.
  lines.push("", "## Writes (collection | flushes | written | unchanged | deleted)");
  for (const write of summary.writes) {
    lines.push(`${write.collection} | ${write.flushes} | ${write.written} | ${write.unchanged} | ${write.deleted}`);
  }

  // Script attribution, where the browser gives it.
  if (summary.frames.topScripts.length > 0) {
    lines.push("", "## Long-frame scripts (source | total)");
    for (const script of summary.frames.topScripts) lines.push(`${script.source} | ${ms(script.durationMs)}`);
  }

  // The costliest bursts, whole.
  lines.push("", "## Worst ticks");
  for (const tick of summary.worst) lines.push(...formatTick(tick));
  return lines.join("\n");
}

/** One tick as a heading line plus its writes, computes and commits. */
export function formatTick(tick: Tick): string[] {
  const writes = Object.entries(tick.writes)
    .map(([collection, count]) => `${collection} ${count.written}w/${count.unchanged}u/${count.deleted}d`)
    .join(", ");
  const computes = tick.computes
    .map((run) => `${run.name} ${ms(run.durationMs)}${run.changed ? "" : " (same)"}`)
    .join(", ");
  const renders = tick.renders.map((commit) => `${commit.id}:${commit.phase} ${ms(commit.durationMs)}`).join(", ");
  return [
    `- #${tick.id} ${ms(tick.durationMs ?? 0)} [${tick.causes.join(", ")}] rows ${tick.rowsRendered}/${tick.rowsSkipped}`,
    `  writes: ${writes || "none"}`,
    `  computes: ${computes || "none"}`,
    `  renders: ${renders || "none"}`,
  ];
}

/** Whether the recorder dropped ticks inside the window, so the summary covers less than it names. */
export function isTruncated(summary: Summary): boolean {
  return summary.coveredMs < summary.windowMs - TRUNCATION_SLACK.toMillis();
}
